import { useState } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Send } from 'lucide-react';
import { Avatar } from '../ui/Avatar';
import { Button } from '../ui/Button';
import { Spinner } from '../ui/Spinner';
import { useTicketNotes, useCreateTicketNote } from '../../hooks/queries/useTickets';
import { CollaboratorLink } from '../collaborators/CollaboratorLink';

interface TicketNotesProps {
  ticketId: string;
  canWrite: boolean;
}

export function TicketNotes({ ticketId, canWrite }: TicketNotesProps) {
  const [draft, setDraft] = useState('');
  const { data: notes, isLoading } = useTicketNotes(ticketId);
  const createNote = useCreateTicketNote();

  function handleSubmit() {
    const content = draft.trim();
    if (!content) return;
    createNote.mutate(
      { ticketId, content },
      { onSuccess: () => setDraft('') },
    );
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit();
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Thread */}
      {notes && notes.length > 0 ? (
        <div className="space-y-3">
          {notes.map((note) => {
            const author = note.author;
            return (
              <div key={note.id} className="flex gap-3">
                <CollaboratorLink collaboratorId={note.author_id} title={author?.full_name ? `Voir le profil de ${author.full_name}` : undefined}>
                  <Avatar
                    name={author?.full_name ?? '?'}
                    src={author?.avatar_url ?? undefined}
                    size="sm"
                    className="flex-shrink-0 mt-0.5"
                  />
                </CollaboratorLink>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <CollaboratorLink collaboratorId={note.author_id} title={author?.full_name ? `Voir le profil de ${author.full_name}` : undefined}>
                      <span className="text-sm font-medium text-neutral-800">
                        {author?.full_name ?? 'Inconnu'}
                      </span>
                    </CollaboratorLink>
                    <span className="text-xs text-neutral-400">
                      {format(new Date(note.created_at), "d MMM yyyy 'à' HH:mm", { locale: fr })}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-neutral-700 whitespace-pre-wrap leading-relaxed bg-neutral-50 rounded-lg px-3 py-2 border border-neutral-100">
                    {note.content}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-sm text-neutral-400 italic">Aucune note pour l'instant.</p>
      )}

      {/* Composer */}
      {canWrite && (
        <div className="space-y-2 pt-1">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ajouter une note interne (⌘+Entrée pour envoyer)..."
            rows={3}
            className="w-full text-sm border border-neutral-300 rounded-cime px-3 py-2 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-cime-primary focus:border-transparent resize-none"
          />
          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={!draft.trim() || createNote.isPending}
              isLoading={createNote.isPending}
            >
              <Send className="w-3.5 h-3.5" />
              Ajouter
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
